// frontend/src/pages/Admin/Fiches.tsx
import { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from 'pages/Page';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TopArticle  { article: string; code: string; count: number }
interface TopConcept  { concept: string; count: number }
interface TopCited    {
  id: string; ecli: string; number: string; chamber: string;
  decision_date: string; probleme: string; citation_count: number;
}

interface Stats {
  total:             number;
  enriched:          number;
  enrichment_pct:    number;
  enriched_v1:       number;
  enriched_v1_pct:   number;
  concepts_total:    number;
  articles_total:    number;
  citations_total:   number;
  qualified_by_type: Record<string, number>;
  quality: {
    avg: number; perfect: number; perfect_pct: number;
    distribution: Record<string, number>;
  };
  domaines:     [string, number][];
  juridictions: [string, number][];
  chambres:     [string, number][];
  solutions:    [string, number][];
  by_year:      [string, number][];
  top_articles: TopArticle[];
  top_concepts: TopConcept[];
  top_cited:    TopCited[];
  legal_graph?: {
    graph_built:           number;
    graph_built_pct:       number;
    has_lineage:           number;
    has_lineage_pct:       number;
    has_ca_renvoi:         number;
    has_ca_renvoi_pct:     number;
    revirements_fiches:    number;
    revirements_total:     number;
    procedure_links_total: number;
  };
}

// ── Nouveaux types Legal Graph ────────────────────────────────────────────────

/** Un lien dans procedure_graph (build_procedure_graph.py) */
interface ProcedureLink {
  link_id:              string;
  link_type:            ProcedureLinkType;
  target:               string;    // numéro ou ref brute
  target_id:            string | null;
  resolved:             boolean;
  confidence:           number;
  source:               string;
  // Hydratés depuis la fiche cible si resolved
  target_ecli?:          string;
  target_number?:        string;
  target_decision_date?: string;
  target_chamber?:       string;
  target_solution?:      string;
  target_jurisdiction?:  string;
  target_probleme?:      string;
  target_domaine?:       string;
}

type ProcedureLinkType =
  | 'casse'
  | 'cassation_sans_renvoi'
  | 'rejette'
  | 'cassation_partielle'
  | 'rapprochement'
  | 'jonction'
  | 'renvoi_apres_cassation';

/** procedure_lineage extrait par LLM (extract_relations.py) */
interface ProcedureLineage {
  arret_attaque?: {
    ref?:         string | null;
    juridiction?: string | null;
    date?:        string | null;
    solution_cc?: string | null;
  } | null;
  ca_renvoi?: {
    juridiction?:      string | null;
    mention_textuelle?: string | null;
  } | null;
  pourvois_joints?:                 string[];
  arrets_anterieurs_meme_affaire?:  { ref: string; etape: string }[];
}

/** Arrêts impactés par cet arrêt (revirements etc.) */
interface ArretImpacte {
  ref:          string;
  impact:       'revirement' | 'restriction' | 'extension' | 'confirmation_renforcee';
  explication:  string;
}

/** Fiche qui cite l'arrêt courant (lien entrant) */
interface CitedByFiche {
  id:              string;
  ecli:            string;
  number:          string;
  decision_date:   string;
  chamber:         string;
  solution:        string;
  domaine:         string;
  importance_score: number;
}

interface Fiche {
  id:                    string;
  score?:                number;
  slug?:                 string;
  ecli:                  string;
  number:                string;
  jurisdiction:          string;
  chamber:               string;
  decision_date:         string;
  solution:              string;
  quality_score:         number;
  domaine:               string;
  sous_domaine:          string;
  mots_cles:             string[];
  concepts:              string[];
  articles:              { code: string; article: string }[];
  cite_arrets:           string[];
  cite_arrets_qualifies: CiteArretQualifie[];
  citation_count:        number;
  graph_extracted_at:    string | null;
  judilibre_id:          string | null;
  publication:           string[];  // ["b"] | ["b","r"] | []
  faits:                 string;
  procedure:             string;
  probleme:              string;
  solution_text:         string;
  themes:                string[];
  // Legal Graph v2
  procedure_graph:       ProcedureLink[];
  graph_built_at:        string | null;
  cited_by_ids:          string[];
  cited_by_count:        number;
  cited_by_types:        Record<string, number>;
  importance_score:      number;
  procedure_lineage:     ProcedureLineage;
  arrets_impactes:       ArretImpacte[];
}

interface CiteArretQualifie {
  ref:            string;
  type:           CiteType;
  contexte:       string;
  found:          boolean;
  fiche_id:       string | null;
  slug?:          string | null;
  decision_date:  string | null;
  chamber:        string | null;
  solution:       string | null;
  probleme:       string | null;
  citation_count: number;
}

type CiteType =
  | 'meme_affaire'
  | 'reference_jurisprudentielle'
  | 'application_principe'
  | 'distinction'
  | 'obiter_dictum'
  | 'revirement'
  | 'confirmation';

interface Relations {
  // LLM
  cite_arrets:            string[];
  cite_arrets_qualifies:  CiteArretQualifie[];
  cited_fiches:           { id: string; ecli: string; number: string; decision_date: string; chamber: string; solution: string; probleme: string; citation_count: number }[];
  arrets_impactes:        ArretImpacte[];
  procedure_lineage:      ProcedureLineage;
  // Graph procédural
  procedure_graph:        ProcedureLink[];
  graph_built_at:         string | null;
  cited_by_fiches:        CitedByFiche[];
  cited_by_count:         number;
  cited_by_types:         Record<string, number>;
  importance_score:       number;
  // Meta
  articles:               { code: string; article: string }[];
  concepts:               string[];
  mots_cles:              string[];
  domaine:                string;
  sous_domaine:           string;
  citation_count:         number;
}

interface Source {
  text:           string;
  legifrance_url: string | null;
}

interface ArticleNeighbor {
  node_id:     string;
  legiarti_id: string | null;
  numero:      string | null;
  code:        string | null;
}

interface ArticleDetail {
  legiarti_id:     string;
  code:            string;
  numero:          string;
  nature:          string;
  classification:  string;
  text:            string;
  section_summary: string;
  questions:       string[];
  legifrance_url:  string;
  prev:            ArticleNeighbor | null;
  next:            ArticleNeighbor | null;
}

type Tab        = 'stats' | 'search' | 'browse';
type ArticleTab = 'text' | 'summary' | 'questions' | 'neighbors';

// ── Constantes ────────────────────────────────────────────────────────────────

const CHAMBER_LABELS: Record<string, string> = {
  comm: 'Ch. commerciale', com: 'Ch. commerciale',
  soc: 'Ch. sociale', 'soc.': 'Ch. sociale',
  civ1: '1re Ch. civile', civ2: '2e Ch. civile', civ3: '3e Ch. civile',
  civ: 'Ch. civile', crim: 'Ch. criminelle', cr: 'Ch. criminelle',
  pl: 'Ass. plénière', mi: 'Ch. mixte',
  fs: 'Formation de section', fp: 'Formation plénière', fr: 'Formation restreinte',
};

const JURISDICTION_LABELS: Record<string, string> = {
  cc: 'Cour de cassation', ca: "Cour d'appel",
  ce: "Conseil d'État", tc: 'Tribunal des conflits',
  tgi: 'TGI', tj: 'Tribunal judiciaire',
};

const expandChamber   = (v: string) => CHAMBER_LABELS[v?.toLowerCase()] ?? v;
const expandJurisdict = (v: string) => JURISDICTION_LABELS[v?.toLowerCase()] ?? v;

const publicationLabel = (pub: string[]): { label: string; color: string } | null => {
  if (!pub || pub.length === 0) return null;
  if (pub.includes('b') && pub.includes('r')) return { label: 'P+R', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' };
  if (pub.includes('b')) return { label: 'P+B', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
  if (pub.includes('r')) return { label: 'P+R', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' };
  return null;
};

const VECTOR_OPTIONS = [
  { value: '',               label: 'Fusion RRF (recommandé)' },
  { value: 'probleme',      label: 'Problème de droit' },
  { value: 'faits',         label: 'Faits' },
  { value: 'solution_text', label: 'Solution' },
  { value: 'procedure',     label: 'Procédure' },
];

const CITE_TYPE_CONFIG: Record<CiteType, { label: string; color: string; icon: string }> = {
  meme_affaire:                { label: 'Même affaire',           color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',       icon: '🔄' },
  reference_jurisprudentielle: { label: 'Réf. jurisprudentielle', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: '⭐' },
  application_principe:        { label: 'Application principe',   color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',    icon: '✅' },
  distinction:                 { label: 'Distinction',            color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: '↗️' },
  obiter_dictum:               { label: 'Obiter dictum',          color: 'bg-muted text-muted-foreground',                                          icon: '💬' },
  revirement:                  { label: 'Revirement',             color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',            icon: '🔁' },
  confirmation:                { label: 'Confirmation',           color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',        icon: '✓' },
};

const PROC_LINK_CONFIG: Record<ProcedureLinkType, { label: string; color: string; icon: string; direction: 'out' | 'in' }> = {
  casse:                    { label: 'Casse',                  color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',            icon: '⚡', direction: 'out' },
  cassation_sans_renvoi:    { label: 'Cassation s/renvoi',     color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',            icon: '⚡', direction: 'out' },
  cassation_partielle:      { label: 'Cassation partielle',    color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: '◑', direction: 'out' },
  rejette:                  { label: 'Rejette le pourvoi',     color: 'bg-muted text-muted-foreground',                                          icon: '✗', direction: 'out' },
  rapprochement:            { label: 'Rapprochement',          color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: '≈', direction: 'out' },
  jonction:                 { label: 'Jonction',               color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',        icon: '⊕', direction: 'out' },
  renvoi_apres_cassation:   { label: 'Renvoi après cassation', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: '↩', direction: 'out' },
};

const IMPACT_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  revirement:              { label: 'Revirement',              color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',            icon: '🔁' },
  restriction:             { label: 'Restriction de portée',   color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: '↘' },
  extension:               { label: 'Extension de portée',     color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',    icon: '↗' },
  confirmation_renforcee:  { label: 'Confirmation renforcée',  color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',        icon: '✓✓' },
};

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, color = 'blue' }: {
  label: string; value: string | number; sub?: string; color?: string;
}) => {
  const colors: Record<string, string> = {
    blue:   'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800',
    green:  'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800',
    purple: 'bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800',
    orange: 'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800',
  };
  return (
    <div className={`rounded-lg border p-4 ${colors[color] || colors.blue}`}>
      <p className="text-sm text-muted-foreground font-medium mb-1">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
};

const ScoreBar = ({ label, count, total, labelWidth = 'w-16' }: {
  label: string; count: number; total: number; labelWidth?: string;
}) => {
  const pct = total > 0 ? Math.round(100 * count / total) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className={`${labelWidth} text-xs text-muted-foreground shrink-0`}>{label}</span>
      <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
        <div className="h-2 bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 text-xs text-muted-foreground shrink-0 text-right">{count.toLocaleString()}</span>
    </div>
  );
};

const YearChart = ({ data }: { data: [string, number][] }) => {
  if (!data || data.length === 0) return (
    <p className="text-sm text-muted-foreground italic">Pas de données temporelles</p>
  );
  const max   = Math.max(...data.map(([, v]) => v));
  const W     = 600;
  const H     = 120;
  const BAR_W = Math.max(4, Math.floor((W - 20) / data.length) - 2);
  const GAP   = Math.max(1, Math.floor((W - 20) / data.length) - BAR_W);
  const labelStep = data.length > 20 ? Math.ceil(data.length / 10) : 1;

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H + 28}`} className="w-full" style={{ minWidth: '320px' }}>
        {data.map(([year, count], i) => {
          const barH = max > 0 ? Math.round((count / max) * H) : 0;
          const x    = 10 + i * (BAR_W + GAP);
          return (
            <g key={year}>
              <rect x={x} y={H - barH} width={BAR_W} height={barH} rx={2}
                className="fill-primary opacity-80 hover:opacity-100 transition-opacity">
                <title>{year} : {count.toLocaleString()} décisions</title>
              </rect>
              {i % labelStep === 0 && (
                <text x={x + BAR_W / 2} y={H + 18} textAnchor="middle" fontSize={9}
                  className="fill-muted-foreground">{year}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ── Badge graphe sur FicheCard ────────────────────────────────────────────────

const GraphBadge = ({ fiche }: { fiche: Fiche }) => {
  const hasProcGraph  = (fiche.procedure_graph?.length ?? 0) > 0;
  const hasLineage    = !!fiche.procedure_lineage && Object.keys(fiche.procedure_lineage).length > 0;
  const hasCitedBy    = (fiche.cited_by_count ?? 0) > 0;
  const hasImpactes   = (fiche.arrets_impactes?.length ?? 0) > 0;

  if (!hasProcGraph && !hasLineage && !hasCitedBy && !hasImpactes && !fiche.graph_extracted_at)
    return null;

  return (
    <div className="flex flex-wrap gap-1">
      {hasProcGraph && <span className="text-[10px] bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 px-1.5 rounded font-medium">⬡ graph</span>}
      {hasLineage   && <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 rounded font-medium">⎇ lineage</span>}
      {hasCitedBy   && <span className="text-[10px] bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 px-1.5 rounded font-medium">↙ {fiche.cited_by_count}</span>}
      {hasImpactes  && <span className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-1.5 rounded font-medium">🔁 rev.</span>}
    </div>
  );
};

const FicheCard = ({ fiche, onClick, showScore = false }: {
  fiche: Fiche; onClick: () => void; showScore?: boolean;
}) => {
  const scoreColor = (s: number) =>
    s >= 0.85 ? 'text-green-600' : s >= 0.70 ? 'text-yellow-600' : s >= 0.55 ? 'text-orange-600' : 'text-red-500';
  const qualityColor = (q: number) =>
    q === 100 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
    q >= 75   ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
    q >= 50   ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';

  return (
    <div onClick={onClick} className="rounded-lg border bg-card p-4 hover:bg-accent cursor-pointer transition-colors group">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {showScore && fiche.score != null && (
              <span className={`text-sm font-bold font-mono ${scoreColor(fiche.score)}`}>
                {fiche.score.toFixed(4)}
              </span>
            )}
            <span className="font-mono text-xs font-semibold truncate max-w-[200px]">
              {fiche.number || fiche.id.slice(0, 8)}
            </span>
            <span className="text-xs text-muted-foreground">·</span>
            <span className="text-xs text-muted-foreground">{fiche.decision_date}</span>
            <span className="text-xs text-muted-foreground">·</span>
            <span className="text-xs text-muted-foreground">{expandJurisdict(fiche.jurisdiction)}</span>
            <span className="text-xs text-muted-foreground">·</span>
            <span className="text-xs text-muted-foreground">{expandChamber(fiche.chamber)}</span>
            {(() => { const p = publicationLabel(fiche.publication); return p ? (
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${p.color}`}>{p.label}</span>
            ) : null; })()}
            {fiche.citation_count > 0 && (
              <span className="text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 px-1.5 rounded font-mono">
                ★ {fiche.citation_count}
              </span>
            )}
          </div>
          <GraphBadge fiche={fiche} />
          {fiche.probleme && (
            <p className="text-sm font-medium mt-2 mb-1 line-clamp-2 text-foreground">{fiche.probleme}</p>
          )}
          <div className="flex flex-wrap gap-1 mt-1">
            {fiche.domaine && (
              <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded">
                {fiche.domaine}
              </span>
            )}
            {fiche.concepts.slice(0, 3).map((c, i) => (
              <span key={i} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">{c}</span>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${qualityColor(fiche.quality_score)}`}>
            {fiche.quality_score}/100
          </span>
          <svg className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

// ── ProcedureLinkCard ─────────────────────────────────────────────────────────

const ProcedureLinkCard = ({ link, onOpenFiche }: {
  link: ProcedureLink;
  onOpenFiche?: (id: string) => void;
}) => {
  const cfg = PROC_LINK_CONFIG[link.link_type] || {
    label: link.link_type, color: 'bg-muted text-muted-foreground', icon: '→',
  };
  return (
    <div className="p-3 rounded-lg border bg-card text-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          {/* Type + confiance */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
              {cfg.icon} {cfg.label}
            </span>
            <span className="text-xs text-muted-foreground/60 font-mono">
              {Math.round(link.confidence * 100)}% · {link.source}
            </span>
          </div>
          {/* Référence cible */}
          <p className="font-medium">{link.target}</p>
          {/* Détails */}
          {link.resolved ? (
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground">
                {expandChamber(link.target_chamber ?? '')}
                {link.target_decision_date ? ` — ${link.target_decision_date}` : ''}
                {link.target_solution ? ` · ${link.target_solution}` : ''}
              </p>
              {link.target_probleme && (
                <p className="text-xs text-muted-foreground italic line-clamp-2">{link.target_probleme}</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground/50 italic">Non indexée dans la base</p>
          )}
        </div>
        {link.resolved && link.target_id && onOpenFiche && (
          <button
            onClick={() => onOpenFiche(link.target_id!)}
            className="shrink-0 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-700 rounded px-2 py-1 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
          >
            Voir →
          </button>
        )}
      </div>
    </div>
  );
};

// ── CiteArretCard ─────────────────────────────────────────────────────────────

const CiteArretCard = ({ cite, onOpenFiche }: {
  cite: CiteArretQualifie;
  onOpenFiche?: (id: string) => void;
}) => {
  const cfg = CITE_TYPE_CONFIG[cite.type] || CITE_TYPE_CONFIG.obiter_dictum;
  return (
    <div className={`p-3 rounded-lg border text-sm ${cite.found ? 'bg-card' : 'bg-muted/30'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-mono text-xs text-muted-foreground">{cite.ref}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
              {cfg.icon} {cfg.label}
            </span>
            {cite.citation_count > 0 && (
              <span className="text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 px-1.5 rounded font-mono">
                ★ {cite.citation_count}
              </span>
            )}
          </div>
          {cite.contexte && (
            <p className="text-xs text-muted-foreground italic mb-1">"{cite.contexte}"</p>
          )}
          {cite.found && (
            <>
              <p className="font-medium text-xs">{cite.chamber} — {cite.decision_date}</p>
              {cite.probleme && (
                <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">{cite.probleme}</p>
              )}
            </>
          )}
        </div>
        {cite.found && cite.fiche_id && onOpenFiche && (
          <button
            onClick={() => onOpenFiche(cite.fiche_id!)}
            className="shrink-0 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-700 rounded px-2 py-1 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
          >
            Voir →
          </button>
        )}
      </div>
    </div>
  );
};

// ── ArticleModal ──────────────────────────────────────────────────────────────

const ArticleModal = ({ code, numero, apiClient, onClose }: {
  code: string; numero: string; apiClient: any; onClose: () => void;
}) => {
  const [detail, setDetail]   = useState<ArticleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [tab, setTab]         = useState<ArticleTab>('text');

  const load = async (c: string, n: string) => {
    setLoading(true); setError(false); setDetail(null);
    try {
      const params = new URLSearchParams({ code: c, numero: n });
      const res = await fetch(apiClient.buildEndpoint(`/api/articles/article-lookup?${params}`));
      if (!res.ok) throw new Error();
      setDetail(await res.json());
      setTab('text');
    } catch { setError(true); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(code, numero); }, [code, numero]);

  const articleTabs: { key: ArticleTab; label: string }[] = [
    { key: 'text',      label: '📜 Texte' },
    { key: 'summary',   label: '📋 Résumé' },
    { key: 'questions', label: '❓ Questions' },
    { key: 'neighbors', label: '🔗 Voisins' },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        <div className="border-b p-4 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground font-mono uppercase tracking-wide truncate">{detail?.code || code}</p>
              <p className="font-semibold text-lg mt-0.5">Art. {detail?.numero || numero}</p>
              {detail?.classification && (
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-2">
                  {detail.classification.replace(/&gt;/g, '›')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {detail?.legifrance_url && (
                <a href={detail.legifrance_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium border border-blue-300 dark:border-blue-700 rounded-md px-2 py-1 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors">
                  Légifrance
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {detail && (
            <div className="flex gap-1 mt-3 flex-wrap">
              {articleTabs.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${tab === t.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="overflow-auto flex-1 p-4">
          {loading && <div className="flex justify-center py-10"><Loader className="!size-6" /></div>}
          {error && (
            <div className="text-center py-10 text-muted-foreground">
              <p className="text-3xl mb-2">⚠️</p>
              <p className="text-sm">Article introuvable dans la base</p>
              <p className="text-xs mt-1 font-mono">{numero} — {code}</p>
            </div>
          )}
          {detail && tab === 'text' && <p className="text-sm leading-relaxed whitespace-pre-wrap">{detail.text}</p>}
          {detail && tab === 'summary' && (
            detail.section_summary
              ? <p className="text-sm leading-relaxed">{detail.section_summary}</p>
              : <p className="text-sm text-muted-foreground italic">Pas de résumé disponible</p>
          )}
          {detail && tab === 'questions' && (
            detail.questions.length > 0
              ? <ol className="space-y-3">{detail.questions.map((q, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-xs font-bold text-muted-foreground mt-0.5 shrink-0 w-5">{i + 1}.</span>
                    <p className="text-sm leading-relaxed">{q}</p>
                  </li>
                ))}</ol>
              : <p className="text-sm text-muted-foreground italic">Pas de questions disponibles</p>
          )}
          {detail && tab === 'neighbors' && (
            <div className="space-y-4">
              {([
                { label: '← Article précédent', neighbor: detail.prev },
                { label: '→ Article suivant',   neighbor: detail.next },
              ] as const).map(({ label, neighbor }) => (
                <div key={label}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{label}</p>
                  {neighbor ? (
                    <button onClick={() => { if (neighbor.numero && neighbor.code) load(neighbor.code, neighbor.numero); }}
                      disabled={!neighbor.numero || !neighbor.code}
                      className="w-full text-left p-3 rounded-lg border bg-muted/30 hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-default">
                      <p className="text-sm font-mono font-medium">Art. {neighbor.numero || '—'}</p>
                      {neighbor.code && <p className="text-xs text-muted-foreground mt-0.5">{neighbor.code}</p>}
                    </button>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Aucun voisin référencé</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── ArticleTag cliquable ──────────────────────────────────────────────────────

const ArticleTag = ({ code, article, apiClient }: { code: string; article: string; apiClient: any }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} title={`Voir l'article ${article} — ${code}`}
        className="inline-flex items-center gap-1 text-sm font-mono bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-3 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800/40 transition-colors cursor-pointer">
        Art. {article} C. {code}
        <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </button>
      {open && <ArticleModal code={code} numero={article} apiClient={apiClient} onClose={() => setOpen(false)} />}
    </>
  );
};

// ── TopCitedCard ──────────────────────────────────────────────────────────────

const TopCitedCard = ({ fiche, onOpen }: { fiche: Fiche; onOpen: (fiche: Fiche) => void }) => (
  <button
    onClick={() => onOpen(fiche)}
    className="w-full text-left p-3 rounded-lg border bg-card hover:bg-accent transition-colors group"
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 px-2 py-0.5 rounded-full font-bold font-mono">
            ★ {fiche.citation_count}
          </span>
          <span className="font-mono text-xs font-semibold text-foreground truncate max-w-[160px]">
            {fiche.number || fiche.id.slice(0, 8)}
          </span>
          <span className="text-xs text-muted-foreground">{expandChamber(fiche.chamber)}</span>
          <span className="text-xs text-muted-foreground">{fiche.decision_date}</span>
          {fiche.domaine && (
            <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded">
              {fiche.domaine}
            </span>
          )}
        </div>
        {fiche.probleme && (
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{fiche.probleme}</p>
        )}
      </div>
      <svg className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  </button>
);

// ── Fiche Detail Modal ────────────────────────────────────────────────────────

const FicheModal = ({ fiche, onClose, apiClient, onOpenFiche }: {
  fiche: Fiche; onClose: () => void; apiClient: any;
  onOpenFiche: (id: string) => void;
}) => {
  const [relations, setRelations]         = useState<Relations | null>(null);
  const [loadingRel, setLoadingRel]       = useState(false);
  const [source, setSource]               = useState<Source | null>(null);
  const [loadingSource, setLoadingSource] = useState(false);
  const [sourceError, setSourceError]     = useState(false);
  const [activeSection, setActiveSection] = useState<'fiche' | 'relations' | 'source'>('fiche');
  const [relTab, setRelTab]               = useState<'graph' | 'lineage' | 'citations' | 'notions'>('graph');

  const loadRelations = async () => {
    setLoadingRel(true);
    try {
      // Les données statiques viennent directement du payload fiche déjà chargé
      const procedure_graph    = fiche.procedure_graph   ?? [];
      const cite_arrets        = fiche.cite_arrets       ?? [];
      const cited_by_ids       = fiche.cited_by_ids      ?? [];
      const resolved_target_ids = procedure_graph
        .filter(l => l.resolved && l.target_id)
        .map(l => l.target_id!);

      // On ne demande au backend que les 3 hydratations (lookups par ID)
      const res = await fetch(apiClient.buildEndpoint(`/api/fiches/${fiche.id}/relations`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cite_arrets, cited_by_ids, resolved_target_ids }),
      });
      if (!res.ok) return;
      const { cited_fiches, targets_by_id, cited_by_fiches } = await res.json();

      // Reconstruction locale de cite_arrets_qualifies enrichi
      const byRef: Record<string, typeof cited_fiches[0]> = {};
      for (const f of cited_fiches) {
        if (f.ecli)   byRef[f.ecli]   = f;
        if (f.number) byRef[f.number] = f;
      }
      const cite_arrets_qualifies_enriched: CiteArretQualifie[] = (fiche.cite_arrets_qualifies ?? []).map(c => {
        const found = byRef[c.ref];
        return {
          ...c,
          found:          !!found,
          fiche_id:       found?.id            ?? null,
          slug:           found?.slug          ?? null,
          decision_date:  found?.decision_date ?? null,
          chamber:        found?.chamber       ?? null,
          solution:       found?.solution      ?? null,
          probleme:       found?.probleme      ?? null,
          citation_count: found?.citation_count ?? 0,
        };
      });

      // Reconstruction locale de procedure_graph enrichi
      const proc_graph_resolved = procedure_graph.map(lnk => {
        const tp = lnk.target_id ? (targets_by_id[lnk.target_id] ?? {}) : {};
        return {
          ...lnk,
          target_ecli:          tp.ecli          ?? '',
          target_number:        tp.number        ?? '',
          target_decision_date: tp.decision_date ?? '',
          target_chamber:       tp.chamber       ?? '',
          target_solution:      tp.solution      ?? '',
          target_jurisdiction:  tp.jurisdiction  ?? '',
          target_probleme:      tp.probleme      ? tp.probleme.slice(0, 200) : '',
          target_domaine:       tp.domaine       ?? '',
        };
      });

      setRelations({
        // Hydraté par le backend
        cited_fiches,
        cited_by_fiches,
        // Reconstruit localement
        cite_arrets:            cite_arrets,
        cite_arrets_qualifies:  cite_arrets_qualifies_enriched,
        procedure_graph:        proc_graph_resolved,
        cited_by_count:         fiche.cited_by_count   ?? 0,
        cited_by_types:         fiche.cited_by_types   ?? {},
        importance_score:       fiche.importance_score ?? 0,
        graph_built_at:         fiche.graph_built_at   ?? null,
        // Directement depuis la fiche
        arrets_impactes:        fiche.arrets_impactes    ?? [],
        procedure_lineage:      fiche.procedure_lineage  ?? {},
        articles:               fiche.articles           ?? [],
        concepts:               fiche.concepts           ?? [],
        mots_cles:              fiche.mots_cles          ?? [],
        domaine:                fiche.domaine            ?? '',
        sous_domaine:           fiche.sous_domaine       ?? '',
        citation_count:         fiche.citation_count     ?? 0,
      });
    } catch (e) { console.error(e); }
    finally { setLoadingRel(false); }
  };

  const loadSource = async () => {
    setLoadingSource(true); setSourceError(false);
    try {
      const params = new URLSearchParams();
      if (fiche.judilibre_id) params.set('judilibre_id', fiche.judilibre_id);
      if (fiche.ecli)         params.set('ecli',         fiche.ecli);
      if (fiche.number)       params.set('number',       fiche.number);
      const res = await fetch(apiClient.buildEndpoint(`/api/fiches/${fiche.id}/source?${params}`));
      if (res.ok) setSource(await res.json());
      else setSourceError(true);
    } catch { setSourceError(true); }
    finally { setLoadingSource(false); }
  };

  useEffect(() => {
    if (activeSection === 'relations' && !relations) loadRelations();
    if (activeSection === 'source'    && !source)    loadSource();
  }, [activeSection]);

  const sections = [
    { key: 'probleme',      label: 'Problème de droit', content: fiche.probleme },
    { key: 'faits',         label: 'Faits',              content: fiche.faits },
    { key: 'procedure',     label: 'Procédure',          content: fiche.procedure },
    { key: 'solution_text', label: 'Solution',           content: fiche.solution_text },
  ].filter(s => s.content);

  const modalTabs: { key: 'fiche' | 'relations' | 'source'; label: string }[] = [
    { key: 'fiche',     label: '📄 Fiche' },
    { key: 'relations', label: '🔗 Relations' },
    { key: 'source',    label: '📜 Texte intégral' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* ── Header ── */}
        <div className="border-b p-5 shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-semibold text-foreground">{fiche.number}</span>
                {fiche.ecli && (
                  <span className="font-mono text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded truncate max-w-[260px]">
                    {fiche.ecli}
                  </span>
                )}
                {fiche.citation_count > 0 && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 px-2 py-0.5 rounded-full font-medium">
                    ★ cité {fiche.citation_count} fois
                  </span>
                )}
                {fiche.importance_score > 0 && (
                  <span className="text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 px-2 py-0.5 rounded-full font-medium">
                    ⬡ impact {fiche.importance_score}
                  </span>
                )}
              </div>
              <p className="font-semibold mt-0.5">
                {expandJurisdict(fiche.jurisdiction)} · {expandChamber(fiche.chamber)} · {fiche.decision_date}
                {(() => { const p = publicationLabel(fiche.publication); return p ? (
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded font-medium ${p.color}`}>{p.label}</span>
                ) : null; })()}
              </p>
              {fiche.domaine && (
                <p className="text-sm text-muted-foreground mt-1">
                  {fiche.domaine}{fiche.sous_domaine ? ` › ${fiche.sous_domaine}` : ''}
                </p>
              )}
              <div className="mt-2">
                <GraphBadge fiche={fiche} />
              </div>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex gap-1 mt-4">
            {modalTabs.map(t => (
              <button key={t.key} onClick={() => setActiveSection(t.key)}
                className={`px-4 py-1.5 text-sm rounded-md transition-colors ${activeSection === t.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Content ── */}
        <div className="overflow-auto flex-1 p-5">

          {/* ── FICHE ── */}
          {activeSection === 'fiche' && (
            <div className="space-y-6">
              {sections.map(s => (
                <div key={s.key}>
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-2">{s.label}</h3>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{s.content}</p>
                </div>
              ))}
              {fiche.themes.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-2">Thèmes</h3>
                  <div className="flex flex-wrap gap-1">
                    {fiche.themes.map((t, i) => (
                      <span key={i} className="text-xs bg-muted px-2 py-1 rounded">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── RELATIONS ── */}
          {activeSection === 'relations' && (
            <div className="flex flex-col gap-3">
              {/* Tabs internes */}
              {!loadingRel && relations && (
                <div className="flex gap-1 border-b">
                  {([
                    { key: 'graph'     as const, label: '⬡ Procédure', count: (relations.procedure_graph?.length ?? 0) + (relations.cited_by_count ?? 0) },
                    { key: 'lineage'   as const, label: '⎇ Historique', count: (relations.arrets_impactes?.length ?? 0) },
                    { key: 'citations' as const, label: '⭐ Citations', count: (relations.cite_arrets?.length ?? 0) },
                    { key: 'notions'   as const, label: '🏷 Notions',  count: (relations.concepts?.length ?? 0) + (relations.articles?.length ?? 0) },
                  ]).map(t => (
                    <button key={t.key} onClick={() => setRelTab(t.key)}
                      className={`px-3 py-1.5 text-xs rounded-t-md -mb-px border-b-2 transition-colors flex items-center gap-1.5 ${relTab === t.key ? 'border-primary text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                      {t.label}
                      {t.count > 0 && <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded-full font-mono">{t.count}</span>}
                    </button>
                  ))}
                </div>
              )}

              {loadingRel ? (
                <div className="flex justify-center py-12"><Loader className="!size-6" /></div>
              ) : relations ? (
                <div className="space-y-5 pt-1">

                  {/* ═══ TAB : GRAPHE ═══ */}
                  {relTab === 'graph' && (
                    <div className="space-y-5">
                      {/* Synthèse */}
                      {(relations.importance_score > 0 || relations.cited_by_count > 0 || (relations.procedure_graph?.length ?? 0) > 0) && (
                        <div className="grid grid-cols-3 gap-3">
                          <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800 text-center">
                            <p className="text-lg font-bold text-indigo-700 dark:text-indigo-300">{relations.importance_score}</p>
                            <p className="text-xs text-muted-foreground">score impact</p>
                          </div>
                          <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 text-center">
                            <p className="text-lg font-bold text-yellow-700 dark:text-yellow-300">{relations.cited_by_count}</p>
                            <p className="text-xs text-muted-foreground">fiches entrantes</p>
                          </div>
                          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 text-center">
                            <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{relations.procedure_graph?.length ?? 0}</p>
                            <p className="text-xs text-muted-foreground">liens procéduraux</p>
                          </div>
                        </div>
                      )}

                      {/* Liens procéduraux sortants */}
                      {(relations.procedure_graph?.length ?? 0) > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">⬡ Liens procéduraux</h3>
                            {relations.graph_built_at && (
                              <span className="text-xs text-muted-foreground opacity-60">
                                · {new Date(relations.graph_built_at).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>
                          <div className="space-y-2">
                            {relations.procedure_graph.map((link, i) => (
                              <ProcedureLinkCard key={link.link_id || i} link={link} onOpenFiche={onOpenFiche} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Fiches entrantes */}
                      {(relations.cited_by_fiches?.length ?? 0) > 0 && (
                        <div>
                          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
                            ↙ Cité par ({relations.cited_by_count})
                            {relations.cited_by_count > 20 && <span className="font-normal"> · top 20</span>}
                          </h3>
                          <div className="space-y-2">
                            {relations.cited_by_fiches.map((cbf, i) => (
                              <div key={cbf.id || i} className="p-3 rounded-lg border bg-card text-sm flex items-center justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-xs text-muted-foreground">{cbf.ecli || cbf.number}</span>
                                    <span className="text-xs text-muted-foreground">{expandChamber(cbf.chamber)}</span>
                                    <span className="text-xs text-muted-foreground">{cbf.decision_date}</span>
                                    {cbf.domaine && <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 rounded">{cbf.domaine}</span>}
                                  </div>
                                </div>
                                <button onClick={() => onOpenFiche(cbf.id)}
                                  className="shrink-0 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-700 rounded px-2 py-1 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors">
                                  Voir →
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!(relations.procedure_graph?.length) && !relations.cited_by_count && (
                        <div className="text-center py-10 text-muted-foreground">
                          <p className="text-3xl mb-2">⬡</p>
                          <p className="text-sm">Graphe procédural non encore construit pour cette fiche</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ═══ TAB : LINEAGE ═══ */}
                  {relTab === 'lineage' && (
                    <div className="space-y-5">
                      {/* Lineage LLM */}
                      {relations.procedure_lineage && Object.keys(relations.procedure_lineage).length > 0 && (() => {
                        const pl = relations.procedure_lineage;
                        const hasAttaque    = !!pl.arret_attaque && (pl.arret_attaque.juridiction || pl.arret_attaque.ref);
                        const hasRenvoi     = !!pl.ca_renvoi?.juridiction;
                        const hasJoints     = (pl.pourvois_joints?.length ?? 0) > 0;
                        const hasAnterieurs = (pl.arrets_anterieurs_meme_affaire?.length ?? 0) > 0;
                        if (!hasAttaque && !hasRenvoi && !hasJoints && !hasAnterieurs) return null;
                        return (
                          <div className="space-y-3">
                            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">⎇ Lineage procédural (LLM)</h3>
                            {hasAttaque && pl.arret_attaque && (
                              <div className="p-3 rounded-lg border bg-muted/20">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Arrêt attaqué</p>
                                <div className="flex flex-wrap gap-2 text-sm">
                                  {pl.arret_attaque.juridiction && <span className="font-medium">{pl.arret_attaque.juridiction}</span>}
                                  {pl.arret_attaque.date        && <span className="text-muted-foreground">{pl.arret_attaque.date}</span>}
                                  {pl.arret_attaque.ref         && <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{pl.arret_attaque.ref}</span>}
                                  {pl.arret_attaque.solution_cc && (
                                    <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-2 py-0.5 rounded-full font-medium">
                                      {pl.arret_attaque.solution_cc}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            {hasRenvoi && pl.ca_renvoi && (
                              <div className="p-3 rounded-lg border bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">↩ Renvoi après cassation</p>
                                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">CA de {pl.ca_renvoi.juridiction}</p>
                                {pl.ca_renvoi.mention_textuelle && (
                                  <p className="text-xs text-muted-foreground italic mt-1">"{pl.ca_renvoi.mention_textuelle}"</p>
                                )}
                              </div>
                            )}
                            {hasJoints && (
                              <div className="p-3 rounded-lg border bg-muted/20">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">⊕ Pourvois joints</p>
                                <div className="flex flex-wrap gap-1">
                                  {pl.pourvois_joints!.map((ref, i) => (
                                    <span key={i} className="font-mono text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded">{ref}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {hasAnterieurs && (
                              <div className="p-3 rounded-lg border bg-muted/20">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Décisions antérieures (même affaire)</p>
                                <div className="space-y-1.5">
                                  {pl.arrets_anterieurs_meme_affaire!.map((a, i) => (
                                    <div key={i} className="flex items-center gap-2 text-xs">
                                      <span className="text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{a.etape}</span>
                                      <span className="font-mono">{a.ref}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Revirements */}
                      {(relations.arrets_impactes?.length ?? 0) > 0 && (
                        <div>
                          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
                            🔁 Jurisprudences impactées ({relations.arrets_impactes.length})
                          </h3>
                          <div className="space-y-2">
                            {relations.arrets_impactes.map((ai, i) => {
                              const cfg = IMPACT_CONFIG[ai.impact] || { label: ai.impact, color: 'bg-muted text-muted-foreground', icon: '→' };
                              return (
                                <div key={i} className="p-3 rounded-lg border bg-card text-sm">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.icon} {cfg.label}</span>
                                    <span className="font-mono text-xs text-muted-foreground">{ai.ref}</span>
                                  </div>
                                  {ai.explication && <p className="text-xs text-muted-foreground italic">{ai.explication}</p>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {!relations.procedure_lineage?.arret_attaque?.juridiction && !(relations.arrets_impactes?.length) && (
                        <div className="text-center py-10 text-muted-foreground">
                          <p className="text-3xl mb-2">⎇</p>
                          <p className="text-sm">Aucun lineage procédural extrait</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ═══ TAB : CITATIONS ═══ */}
                  {relTab === 'citations' && (
                    <div className="space-y-5">
                      {(relations.cite_arrets_qualifies?.length ?? 0) > 0 ? (
                        <div>
                          <div className="flex flex-wrap gap-1 mb-3">
                            {(Object.entries(CITE_TYPE_CONFIG) as [CiteType, typeof CITE_TYPE_CONFIG[CiteType]][]).map(([type, cfg]) => {
                              const count = relations.cite_arrets_qualifies.filter(c => c.type === type).length;
                              if (count === 0) return null;
                              return (
                                <span key={type} className={`text-xs px-2 py-0.5 rounded-full ${cfg.color}`}>
                                  {cfg.icon} {cfg.label} ({count})
                                </span>
                              );
                            })}
                          </div>
                          <div className="space-y-2">
                            {relations.cite_arrets_qualifies.map((cite, i) => (
                              <CiteArretCard key={i} cite={cite} onOpenFiche={onOpenFiche} />
                            ))}
                          </div>
                        </div>
                      ) : (relations.cite_arrets?.length ?? 0) > 0 ? (
                        <div className="space-y-2">
                          {relations.cite_arrets.map((ecli, i) => {
                            const found = relations.cited_fiches?.find(f => f.ecli === ecli || f.number === ecli);
                            return (
                              <div key={i} className={`p-3 rounded-lg border text-sm ${found ? 'bg-green-50 dark:bg-green-950/20 border-green-300' : 'bg-muted/50'}`}>
                                <p className="font-mono text-xs text-muted-foreground">{ecli}</p>
                                {found && (
                                  <>
                                    <p className="font-medium mt-1">{found.chamber} — {found.decision_date}</p>
                                    {found.probleme && <p className="text-muted-foreground mt-0.5 line-clamp-2">{found.probleme}</p>}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-10 text-muted-foreground">
                          <p className="text-3xl mb-2">⭐</p>
                          <p className="text-sm">Aucune citation extraite</p>
                        </div>
                      )}
                      {/* Citation count banner */}
                      {(relations.citation_count ?? 0) > 0 && (
                        <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
                          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                            ★ Cet arrêt est cité par <span className="font-bold">{relations.citation_count}</span> autre{relations.citation_count > 1 ? 's' : ''} décision{relations.citation_count > 1 ? 's' : ''} dans la base
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ═══ TAB : NOTIONS ═══ */}
                  {relTab === 'notions' && (
                    <div className="space-y-5">
                      {(relations.concepts?.length ?? 0) > 0 && (
                        <div>
                          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">Concepts juridiques</h3>
                          <div className="flex flex-wrap gap-2">
                            {relations.concepts.map((c, i) => (
                              <span key={i} className="text-sm bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-3 py-1 rounded-full">{c}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {(relations.articles?.length ?? 0) > 0 && (
                        <div>
                          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">Articles visés</h3>
                          <div className="flex flex-wrap gap-2">
                            {relations.articles.map((a, i) => (
                              <ArticleTag key={i} code={a.code} article={a.article} apiClient={apiClient} />
                            ))}
                          </div>
                        </div>
                      )}
                      {(relations.mots_cles?.length ?? 0) > 0 && (
                        <div>
                          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">Mots-clés</h3>
                          <div className="flex flex-wrap gap-1">
                            {relations.mots_cles.map((m, i) => (
                              <span key={i} className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">{m}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {!(relations.concepts?.length) && !(relations.articles?.length) && (
                        <div className="text-center py-10 text-muted-foreground">
                          <p className="text-3xl mb-2">🏷</p>
                          <p className="text-sm">Aucune notion extraite</p>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <p>Erreur lors du chargement des relations</p>
                </div>
              )}
            </div>
          )}

          {/* ── TEXTE INTÉGRAL ── */}
          {activeSection === 'source' && (
            <div className="space-y-4">
              {loadingSource ? (
                <div className="flex justify-center py-12"><Loader className="!size-6" /></div>
              ) : sourceError ? (
                <div className="text-center py-16 text-muted-foreground">
                  <p className="text-4xl mb-3">⚠️</p>
                  <p>Texte introuvable dans la collection <code className="bg-muted px-1 rounded">jurisprudences</code></p>
                </div>
              ) : source ? (
                <>
                  {source.legifrance_url && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                      <span className="text-sm text-muted-foreground">
                        Pourvoi n° <span className="font-mono font-medium text-foreground">{fiche.number || fiche.ecli}</span>
                      </span>
                      <a href={source.legifrance_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors">
                        Voir sur courdecassation.fr
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  )}
                  <div className="rounded-lg border bg-muted/20 p-4 overflow-auto max-h-[55vh]">
                    <pre className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-foreground">{source.text}</pre>
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const FichesContent = () => {
  const { user, isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate  = useNavigate();

  const [activeTab, setActiveTab]             = useState<Tab>('stats');
  const [stats, setStats]                     = useState<Stats | null>(null);
  const [loadingStats, setLoadingStats]       = useState(false);
  const [topCited, setTopCited]               = useState<Fiche[]>([]);
  const [loadingTopCited, setLoadingTopCited] = useState(false);

  const [query, setQuery]                 = useState('');
  const [vectorMode, setVectorMode]       = useState('');
  const [searching, setSearching]         = useState(false);
  const [searchResults, setSearchResults] = useState<Fiche[]>([]);
  const [searchDone, setSearchDone]       = useState(false);

  const [browseResults, setBrowseResults]         = useState<Fiche[]>([]);
  const [browseTotal, setBrowseTotal]             = useState(0);
  const [browsePage, setBrowsePage]               = useState(0);
  const [browseLoading, setBrowseLoading]         = useState(false);
  const [filterJuridiction, setFilterJuridiction] = useState('');
  const [filterDomaine, setFilterDomaine]         = useState('');
  const [filterEnriched, setFilterEnriched]       = useState(false);
  const [filterQuality, setFilterQuality]         = useState(0);

  const [ficheStack, setFicheStack] = useState<Fiche[]>([]);

  const LIMIT = 20;

  useEffect(() => {
    if (isReady && !isAuthenticated) navigate('/login');
  }, [isReady, isAuthenticated, navigate]);

  useEffect(() => {
    if (activeTab === 'stats' && !stats && isAuthenticated) {
      setLoadingStats(true);
      fetch(apiClient.buildEndpoint('/api/fiches/stats'))
        .then(r => r.json()).then(setStats).catch(console.error)
        .finally(() => setLoadingStats(false));
    }
  }, [activeTab, isAuthenticated]);

  useEffect(() => {
    if (activeTab === 'stats' && topCited.length === 0 && isAuthenticated) {
      setLoadingTopCited(true);
      fetch(apiClient.buildEndpoint('/api/fiches/stats/top-cited'))
        .then(r => r.json())
        .then(data => setTopCited(data.results || []))
        .catch(console.error)
        .finally(() => setLoadingTopCited(false));
    }
  }, [activeTab, isAuthenticated]);

  const loadBrowse = useCallback(async (page = 0) => {
    setBrowseLoading(true);
    const params = new URLSearchParams({ limit: String(LIMIT), offset: String(page * LIMIT) });
    if (filterJuridiction) params.set('jurisdiction',  filterJuridiction);
    if (filterDomaine)     params.set('domaine',       filterDomaine);
    if (filterEnriched)    params.set('enriched_only', 'true');
    if (filterQuality > 0) params.set('quality_min',   String(filterQuality));
    try {
      const res  = await fetch(apiClient.buildEndpoint(`/api/fiches/browse?${params}`));
      const data = await res.json();
      setBrowseResults(data.results || []);
      setBrowseTotal(data.total || 0);
      setBrowsePage(page);
    } catch (e) { console.error(e); }
    finally { setBrowseLoading(false); }
  }, [filterJuridiction, filterDomaine, filterEnriched, filterQuality, apiClient]);

  useEffect(() => {
    if (activeTab === 'browse' && isAuthenticated) loadBrowse(0);
  }, [activeTab, isAuthenticated, filterJuridiction, filterDomaine, filterEnriched, filterQuality]);

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true); setSearchDone(false);
    try {
      const res  = await fetch(apiClient.buildEndpoint('/api/fiches/search'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, vector: vectorMode || null, limit: 10 }),
      });
      const data = await res.json();
      setSearchResults(data.results || []);
      setSearchDone(true);
    } catch (e) { console.error(e); }
    finally { setSearching(false); }
  };

  const openFicheById = async (id: string) => {
    try {
      const res  = await fetch(apiClient.buildEndpoint(`/api/fiches/${id}`));
      const data = await res.json();
      setFicheStack(s => [...s, data]);
    } catch (e) { console.error(e); }
  };

  if (!isReady) return <div className="flex items-center justify-center h-full"><Loader className="!size-6" /></div>;
  if (!isAuthenticated || !user) return null;

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'stats',  label: 'Statistiques', icon: '📊' },
    { key: 'search', label: 'Recherche',    icon: '🔍' },
    { key: 'browse', label: 'Explorer',     icon: '📂' },
  ];

  return (
    <div className="flex flex-col h-full w-full p-6 overflow-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-1">⚖️ Knowledge Base Juridique</h1>
        <p className="text-muted-foreground">Fiches d'arrêt • Recherche sémantique • Legal Graph</p>
      </div>

      <div className="flex gap-1 mb-6 border-b pb-0">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-5 py-2.5 text-sm font-medium rounded-t-md -mb-px border transition-colors ${activeTab === t.key ? 'bg-card border-border border-b-card text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── STATS ── */}
      {activeTab === 'stats' && (
        <div className="space-y-6">
          {loadingStats ? (
            <div className="flex justify-center py-16"><Loader className="!size-8" /></div>
          ) : stats ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Fiches indexées"     value={(stats.total ?? 0).toLocaleString()} color="blue" />
                <StatCard label="Enrichies (graph)"
                  value={`${stats.enrichment_pct ?? 0}%`}
                  sub={`${(stats.enriched ?? 0).toLocaleString()} fiches`}
                  color="purple" />
                <StatCard label="Score qualité moyen" value={`${stats.quality?.avg ?? 0}/100`}
                  sub={`${stats.quality?.perfect_pct ?? 0}% parfaits`} color="green" />
                <StatCard label="Relations extraites"
                  value={((stats.concepts_total ?? 0) + (stats.articles_total ?? 0) + (stats.citations_total ?? 0)).toLocaleString()}
                  sub={`${(stats.concepts_total ?? 0).toLocaleString()} concepts · ${(stats.articles_total ?? 0).toLocaleString()} articles · ${(stats.citations_total ?? 0).toLocaleString()} citations`}
                  color="orange" />
              </div>

              {Object.keys(stats.qualified_by_type || {}).length > 0 && (
                <div className="rounded-lg border bg-card p-5">
                  <h3 className="font-semibold mb-4">Liens qualifiés — Legal Graph</h3>
                  <div className="space-y-2">
                    {(Object.entries(CITE_TYPE_CONFIG) as [CiteType, typeof CITE_TYPE_CONFIG[CiteType]][]).map(([type, cfg]) => {
                      const count = (stats.qualified_by_type || {})[type] || 0;
                      const total = Object.values(stats.qualified_by_type || {}).reduce((a, b) => a + b, 0);
                      return <ScoreBar key={type} label={`${cfg.icon} ${cfg.label}`} count={count} total={total} labelWidth="w-48" />;
                    })}
                  </div>
                </div>
              )}

              {/* ── Legal Graph v2 ── */}
              {stats.legal_graph && (
                <div className="rounded-lg border bg-card p-5">
                  <h3 className="font-semibold mb-1">⬡ Legal Graph v2 — couverture</h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    Métriques issues de <code className="bg-muted px-1 rounded">build_procedure_graph</code> et <code className="bg-muted px-1 rounded">extract_relations</code>
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
                    <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800">
                      <p className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
                        {stats.legal_graph.graph_built_pct}%
                      </p>
                      <p className="text-xs text-muted-foreground">graphe construit</p>
                      <p className="text-xs font-mono text-indigo-600 dark:text-indigo-400 mt-0.5">
                        {stats.legal_graph.graph_built.toLocaleString()} fiches
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                        {stats.legal_graph.has_lineage_pct}%
                      </p>
                      <p className="text-xs text-muted-foreground">avec lineage LLM</p>
                      <p className="text-xs font-mono text-blue-600 dark:text-blue-400 mt-0.5">
                        {stats.legal_graph.has_lineage.toLocaleString()} fiches
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
                      <p className="text-lg font-bold text-yellow-700 dark:text-yellow-300">
                        {stats.legal_graph.has_ca_renvoi_pct}%
                      </p>
                      <p className="text-xs text-muted-foreground">CA renvoi détectée</p>
                      <p className="text-xs font-mono text-yellow-600 dark:text-yellow-400 mt-0.5">
                        {stats.legal_graph.has_ca_renvoi.toLocaleString()} fiches
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">
                        {stats.legal_graph.revirements_fiches.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">fiches avec revirement</p>
                      <p className="text-xs font-mono text-red-600 dark:text-red-400 mt-0.5">
                        {stats.legal_graph.revirements_total.toLocaleString()} arrêts impactés
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 col-span-2 md:col-span-1">
                      <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
                        {stats.legal_graph.procedure_links_total.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">liens procéduraux totaux</p>
                      <p className="text-xs font-mono text-purple-600 dark:text-purple-400 mt-0.5">
                        moy. {stats.legal_graph.graph_built > 0
                          ? (stats.legal_graph.procedure_links_total / stats.legal_graph.graph_built).toFixed(1)
                          : '—'} liens/fiche
                      </p>
                    </div>
                  </div>
                  {/* Barres de couverture */}
                  <div className="space-y-2">
                    {[
                      { label: '⬡ Graphe construit',  count: stats.legal_graph.graph_built,    pct: stats.legal_graph.graph_built_pct },
                      { label: '⎇ Lineage LLM',       count: stats.legal_graph.has_lineage,    pct: stats.legal_graph.has_lineage_pct },
                      { label: '↩ CA renvoi',          count: stats.legal_graph.has_ca_renvoi,  pct: stats.legal_graph.has_ca_renvoi_pct },
                      { label: '🔁 Revirements',       count: stats.legal_graph.revirements_fiches, pct: Math.round(100 * stats.legal_graph.revirements_fiches / Math.max(stats.total, 1)) },
                    ].map(({ label, count, pct }) => (
                      <div key={label} className="flex items-center gap-3">
                        <span className="w-40 text-xs text-muted-foreground shrink-0">{label}</span>
                        <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                          <div className="h-2 bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-24 text-xs text-muted-foreground shrink-0 text-right font-mono">
                          {(count ?? 0).toLocaleString()} ({pct ?? 0}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(stats.by_year ?? []).length > 0 && (
                <div className="rounded-lg border bg-card p-5">
                  <h3 className="font-semibold mb-1">Évolution temporelle</h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    {stats.by_year[0]?.[0]} → {stats.by_year[stats.by_year.length - 1]?.[0]} —{' '}
                    {stats.by_year.length} années couvertes
                  </p>
                  <YearChart data={stats.by_year} />
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div className="rounded-lg border bg-card p-5">
                  <h3 className="font-semibold mb-4">Distribution des scores qualité</h3>
                  <div className="space-y-3">
                    {Object.entries(stats.quality?.distribution ?? {}).map(([label, count]) => (
                      <ScoreBar key={label} label={label} count={count as number} total={stats.total ?? 0} />
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border bg-card p-5">
                  <h3 className="font-semibold mb-4">Répartition par domaine</h3>
                  {(stats.domaines?.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">Pas encore de données</p>
                  ) : (
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {(stats.domaines ?? []).slice(0, 15).map(([d, count]) => (
                        <ScoreBar key={d} label={d.length > 18 ? d.slice(0, 16) + '…' : d} count={count} total={stats.total ?? 0} />
                      ))}
                    </div>
                  )}
                </div>
                <div className="rounded-lg border bg-card p-5">
                  <h3 className="font-semibold mb-4">Par juridiction</h3>
                  <div className="space-y-2">
                    {(stats.juridictions ?? []).map(([j, count]) => (
                      <ScoreBar key={j} label={j.toUpperCase()} count={count} total={stats.total ?? 0} />
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border bg-card p-5">
                  <h3 className="font-semibold mb-4">Par chambre</h3>
                  {(stats.chambres ?? []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">Pas encore de données</p>
                  ) : (
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {(stats.chambres ?? []).slice(0, 15).map(([ch, count]) => (
                        <ScoreBar key={ch} label={ch.length > 18 ? ch.slice(0, 16) + '…' : ch} count={count} total={stats.total ?? 0} />
                      ))}
                    </div>
                  )}
                </div>
                <div className="rounded-lg border bg-card p-5">
                  <h3 className="font-semibold mb-4">Types de solution</h3>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {(stats.solutions ?? []).map(([s, count]) => (
                      <ScoreBar key={s} label={s.length > 18 ? s.slice(0, 16) + '…' : s} count={count} total={stats.total ?? 0} />
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border bg-card p-5">
                  <h3 className="font-semibold mb-4">Top concepts juridiques</h3>
                  {(stats.top_concepts ?? []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">Pas encore de données</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto">
                      {stats.top_concepts.map(({ concept, count }) => (
                        <span key={concept} title={`${count.toLocaleString()} occurrences`}
                          className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-2.5 py-1 rounded-full">
                          {concept}
                          <span className="font-bold opacity-70">{count}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {(stats.top_articles ?? []).length > 0 && (
                <div className="rounded-lg border bg-card p-5">
                  <h3 className="font-semibold mb-4">Top articles de loi cités</h3>
                  <div className="flex flex-wrap gap-2">
                    {stats.top_articles.map(({ article, code, count }) => (
                      <div key={`${article}-${code}`} className="relative">
                        <ArticleTag code={code} article={article} apiClient={apiClient} />
                        <span className="absolute -top-1.5 -right-1.5 text-[10px] bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center font-bold leading-none"
                          title={`${count} citations`}>
                          {count > 99 ? '99+' : count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(loadingTopCited || topCited.length > 0) && (
                <div className="rounded-lg border bg-card p-5">
                  <h3 className="font-semibold mb-1">Arrêts les plus influents</h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    Top {topCited.length || (stats.top_cited ?? []).length} par nombre de citations dans la base
                  </p>
                  {loadingTopCited ? (
                    <div className="flex justify-center py-6"><Loader className="!size-5" /></div>
                  ) : (
                    <div className="space-y-2">
                      {topCited.map(fiche => (
                        <TopCitedCard key={fiche.id} fiche={fiche} onOpen={f => setFicheStack(s => [...s, f])} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <button onClick={() => {
                  setStats(null); setTopCited([]);
                  setLoadingStats(true);
                  fetch(apiClient.buildEndpoint('/api/fiches/stats'))
                    .then(r => r.json()).then(setStats).finally(() => setLoadingStats(false));
                }} className="text-sm text-muted-foreground hover:text-foreground underline">
                  ↺ Rafraîchir
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-16 text-muted-foreground">
              <p className="text-4xl mb-3">⚠️</p>
              <p>Impossible de charger les stats. Qdrant est-il accessible ?</p>
            </div>
          )}
        </div>
      )}

      {/* ── SEARCH ── */}
      {activeTab === 'search' && (
        <div className="space-y-5">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <input type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Ex: licenciement sans cause réelle et sérieuse..."
              className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              autoFocus />
            <select value={vectorMode} onChange={e => setVectorMode(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {VECTOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button type="submit" disabled={searching || !query.trim()}
              className="h-10 px-6 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2">
              {searching ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Recherche...</> : '🔍 Rechercher'}
            </button>
          </form>
          {searching && <div className="flex justify-center py-12"><Loader className="!size-6" /></div>}
          {!searching && searchDone && (
            <>
              <p className="text-sm text-muted-foreground">
                {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} — mode :{' '}
                <span className="font-medium">{VECTOR_OPTIONS.find(o => o.value === vectorMode)?.label}</span>
              </p>
              {searchResults.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground"><p className="text-4xl mb-3">🔎</p><p>Aucun résultat</p></div>
              ) : (
                <div className="space-y-3">
                  {searchResults.map(f => (
                    <FicheCard key={f.id} fiche={f} onClick={() => setFicheStack([f])} showScore />
                  ))}
                </div>
              )}
            </>
          )}
          {!searching && !searchDone && (
            <div className="text-center py-16 text-muted-foreground">
              <p className="text-5xl mb-4">⚖️</p>
              <p className="font-medium mb-2">Recherche sémantique dans la jurisprudence</p>
              <p className="text-sm">Embeddings Qwen3 sur 4 vecteurs (faits, procédure, problème, solution)</p>
            </div>
          )}
        </div>
      )}

      {/* ── BROWSE ── */}
      {activeTab === 'browse' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3 p-4 rounded-lg border bg-muted/30">
            <select value={filterJuridiction} onChange={e => setFilterJuridiction(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm">
              <option value="">Toutes juridictions</option>
              <option value="cc">Cour de cassation</option>
              <option value="ca">Cour d'appel</option>
            </select>
            <input type="text" value={filterDomaine} onChange={e => setFilterDomaine(e.target.value)}
              placeholder="Domaine (ex: droit social)"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm" />
            <label className="flex items-center gap-2 h-9 text-sm cursor-pointer">
              <input type="checkbox" checked={filterEnriched} onChange={e => setFilterEnriched(e.target.checked)} className="rounded" />
              Enrichies seulement
            </label>
            <select value={filterQuality} onChange={e => setFilterQuality(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm">
              <option value={0}>Toute qualité</option>
              <option value={75}>Score ≥ 75</option>
              <option value={100}>Score = 100</option>
            </select>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {browseTotal.toLocaleString()} fiche{browseTotal > 1 ? 's' : ''} • page {browsePage + 1} / {Math.max(1, Math.ceil(browseTotal / LIMIT))}
            </p>
            <div className="flex gap-2">
              <button disabled={browsePage === 0 || browseLoading} onClick={() => loadBrowse(browsePage - 1)}
                className="px-3 py-1.5 text-sm rounded-md border hover:bg-accent disabled:opacity-40">← Préc.</button>
              <button disabled={(browsePage + 1) * LIMIT >= browseTotal || browseLoading} onClick={() => loadBrowse(browsePage + 1)}
                className="px-3 py-1.5 text-sm rounded-md border hover:bg-accent disabled:opacity-40">Suiv. →</button>
            </div>
          </div>
          {browseLoading ? (
            <div className="flex justify-center py-12"><Loader className="!size-6" /></div>
          ) : browseResults.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground"><p className="text-4xl mb-3">📂</p><p>Aucune fiche trouvée</p></div>
          ) : (
            <div className="space-y-3">
              {browseResults.map(f => (
                <FicheCard key={f.id} fiche={f} onClick={() => setFicheStack([f])} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── MODAL STACK ── */}
      {ficheStack.map((fiche, idx) => (
        <FicheModal
          key={fiche.id + idx}
          fiche={fiche}
          apiClient={apiClient}
          onClose={() => setFicheStack(s => s.slice(0, idx))}
          onOpenFiche={openFicheById}
        />
      ))}
    </div>
  );
};

export default function Fiches() {
  return <Page><FichesContent /></Page>;
}